import { ref } from 'vue'
import { loadStripe } from '@stripe/stripe-js'
import type { Stripe, StripeElements } from '@stripe/stripe-js'
import { createPaymentIntent } from '@/api/registration'

function extractApiErrorMessage(e: unknown): string {
  if (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  ) {
    const response = (
      e as {
        response: {
          status: number
          data: { message?: string; errors?: Record<string, string[]> }
        }
      }
    ).response
    if (response.data?.errors) {
      const first = Object.values(response.data.errors)[0]
      if (first?.[0]) return first[0]
    }
    if (response.data?.message) return response.data.message
  }
  return 'registration.payment.error_generic'
}

export function useStripePayment() {
  const isLoadingIntent = ref<boolean>(false)
  const isProcessingPayment = ref<boolean>(false)
  const paymentError = ref<string | null>(null)
  const stripeReady = ref<boolean>(false)

  let stripe: Stripe | null = null
  let elements: StripeElements | null = null

  async function initializePayment(registrationId: number, mountElementId: string): Promise<void> {
    isLoadingIntent.value = true
    paymentError.value = null
    try {
      // Step 1: create the PaymentIntent via the backend API
      let intentData: { client_secret: string; stripe_publishable_key: string; stripe_account_id: string }
      try {
        intentData = await createPaymentIntent(registrationId)
      } catch (e: unknown) {
        paymentError.value = extractApiErrorMessage(e)
        return
      }

      const { client_secret, stripe_publishable_key, stripe_account_id } = intentData

      // Step 2: load Stripe.js and mount the Payment Element
      const stripeOptions = stripe_account_id ? { stripeAccount: stripe_account_id } : {}
      stripe = await loadStripe(stripe_publishable_key, stripeOptions)
      if (!stripe) {
        paymentError.value = 'registration.payment.error_load'
        return
      }
      elements = stripe.elements({ clientSecret: client_secret })
      const paymentElement = elements.create('payment')
      paymentElement.on('ready', () => {
        stripeReady.value = true
      })
      paymentElement.on('loaderror', (event) => {
        paymentError.value = event.error?.message ?? 'registration.payment.error_load'
      })
      paymentElement.mount(`#${mountElementId}`)
    } catch {
      paymentError.value = 'registration.payment.error_load'
    } finally {
      isLoadingIntent.value = false
    }
  }

  async function confirmPayment(returnUrl: string): Promise<void> {
    if (!stripe || !elements) return
    isProcessingPayment.value = true
    paymentError.value = null
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    })
    if (error) {
      // Raw Stripe error messages contain spaces; i18n fallback key does not
      paymentError.value = error.message ?? 'registration.payment.error_generic'
      isProcessingPayment.value = false
    }
    // On success Stripe performs a browser redirect — no further code runs
  }

  function destroy(): void {
    elements = null
    stripe = null
    stripeReady.value = false
  }

  return { isLoadingIntent, isProcessingPayment, paymentError, stripeReady, initializePayment, confirmPayment, destroy }
}
